import React, { useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, AlertCircle, Info, FolderOpen, Save, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { Header, calcStatusToBadge } from "@/components/jackstone/Header";
import { SectionCard } from "@/components/jackstone/SectionCard";
import { CalcFieldRow, FieldNote, FOCUS_RING } from "@/components/jackstone/CalcFieldRow";
import { ExportReportCard } from "@/components/jackstone/ExportReportCard";
import { useCalculator } from "@/hooks/useCalculator";
import { fmt, fmtInt, fmtRecirc, fmtSmart } from "@/lib/format";
import { buildProjectFile, triggerDownload, parseProjectInputs } from "@/lib/project";
import { saveScenario, getClientId } from "@/lib/api";
import { buildGanttExportPayload } from "@/components/gantt/ganttTypes";
import { stripOverride } from "@/lib/autocorrect";
import { useSettings } from "@/hooks/useSettings";
import { useUnitInput } from "@/hooks/useUnitInput";
import {
  formatBlockSize, formatQty, localiseMessage, localiseNote, siInputToUS, unitLabel,
  type Quantity, type UnitSystem,
} from "@/lib/units";

export default function Calculator() {
  const {
    inputs, set, blur, notes, manual, setManual, resetInputs, clearInputs, productOptions,
    primaryRefrigerantOptions, heatTransferFluidOptions,
    htfOutletTemp,
    result: r, vpfDimensions: vpf, status, stale, banner, refDataError, fieldErrors,
  } = useCalculator();

  // Save / export only from results that match the current inputs
  const current = !!r && status === "ok";

  const navigate = useNavigate();
  const isSecondary = inputs.refrigerantType === "Secondary";

  // Sub-total bars — sourced from backend tLoad / tUnload
  const totalLoadingMin   = r?.tLoad   ?? null;
  const totalUnloadingMin = r?.tUnload ?? null;

  // ── Save / Open project ────────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);

  const defaultFilename = useMemo(() => {
    if (!r) return "";
    const date = new Date().toISOString().split("T")[0];
    const nFr  = (inputs.nFreezers || "?").replace(/!/g, "");
    return `${date} - ${nFr}x - ${r.modelCode}`;
  }, [r, inputs.nFreezers]);

  const handleSave = useCallback(() => {
    if (!r || status !== "ok") return;
    triggerDownload(buildProjectFile(inputs, r, vpf));
    // Additive, fire-and-forget — persist the full scenario (inputs + outputs +
    // charts) to MongoDB. A failure must never affect the JSON download.
    (async () => {
      try {
        const { params, model_code, n_freezers } = await buildGanttExportPayload();
        await saveScenario({
          params,
          model_code,
          label: `${n_freezers} x ${model_code}`,
          page: "calculator",
          trigger: "save",
          session_id: getClientId(),
          calc_inputs: inputs,
          calc_outputs: {
            nominalCapacityTotal: r.nominalCapacityTotal,
            overallCoolingLoad: r.overallCoolingLoad,
          },
        });
      } catch (err) {
        console.warn("Scenario save failed (ignored):", err);
      }
    })();
  }, [inputs, r, vpf, status]);

  const handleOpen = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        // Validated all-or-nothing against the loaded reference lists
        const partial = parseProjectInputs(ev.target?.result as string, {
          products: productOptions.map(o => o.value),
          primaryRefrigerants: primaryRefrigerantOptions.map(o => o.value),
          secondaryRefrigerants: heatTransferFluidOptions.map(o => o.value),
        });
        resetInputs(partial);
        toast.success(`Opened ${file.name}`);
      } catch (err: any) {
        toast.error("Could not open the project file — nothing was changed.", {
          description: err?.message ?? "Please check the file format.",
          duration: 8000,
        });
      }
    };
    reader.onerror = () => toast.error("Could not read the project file.");
    reader.readAsText(file);
    e.target.value = "";   // allow re-opening the same file
  }, [resetInputs, productOptions, primaryRefrigerantOptions, heatTransferFluidOptions]);

  const out = (v: number | null | undefined, dec = 1) => (r ? fmtSmart(v, dec) : "\u2014");
  const outInt = (v: number | null | undefined) => (r ? fmtInt(v) : "\u2014");

  // ── Display units (Settings → Interface units). State stays SI; these only
  //    change what is shown. SI formatting is passed through unchanged.
  const { uiUnits } = useSettings();
  const u = (q: Quantity) => unitLabel(q, uiUnits);
  const outQ = (v: number | null | undefined, q: Quantity, dec = 1) =>
    (r ? formatQty(v, q, uiUnits, (x) => fmtSmart(x, dec)) : "\u2014");
  const outQInt = (v: number | null | undefined, q: Quantity) =>
    (r ? formatQty(v, q, uiUnits, fmtInt) : "\u2014");
  const vpfQInt = (v: number | undefined, q: Quantity) =>
    (vpf ? formatQty(v, q, uiUnits, fmtInt) : "\u2014");

  return (
    <div className="min-h-screen calc-grid-bg">
      <Header
        status={calcStatusToBadge(status)}
        modelCode={r?.modelCode}
        activePage="calculator"
      />

      <main className={`mx-auto max-w-[1320px] px-4 sm:px-6 py-5${stale ? " calc-stale" : ""}`}>
        {/* reference data failed to load */}
        {refDataError && (
          <div
            data-testid="ref-data-banner"
            role="alert"
            className="mb-4 rounded-lg border border-[rgba(179,0,53,0.35)] bg-[rgba(179,0,53,0.06)] px-4 py-3"
          >
            <div className="flex items-center gap-2 text-[var(--js-red)]">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="text-sm font-semibold">{refDataError}</span>
            </div>
          </div>
        )}

        {/* server unreachable — distinct from invalid input */}
        {status === "error" && (
          <div
            data-testid="offline-banner"
            role="alert"
            className="mb-4 rounded-lg border border-[rgba(179,0,53,0.35)] bg-[rgba(179,0,53,0.06)] px-4 py-2.5"
          >
            <div className="flex items-center gap-2 text-[var(--js-red)]">
              <WifiOff className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="text-sm font-semibold">Can&apos;t reach the server — retrying on your next edit</span>
            </div>
          </div>
        )}

        {/* stale results — outputs are greyed (.calc-stale in index.css) */}
        {stale && r && status !== "error" && (
          <div
            data-testid="stale-banner"
            role="status"
            className="mb-4 rounded-lg border border-[rgba(245,166,35,0.55)] bg-[rgba(245,166,35,0.12)] px-4 py-2.5"
          >
            <div className="flex items-center gap-2 text-[#8a5900]">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="text-sm font-semibold">Results out of date — fix highlighted fields</span>
            </div>
          </div>
        )}

        {/* error banner */}
        {banner.length > 0 && (
          <div
            data-testid="error-banner"
            className="mb-4 rounded-lg border border-[rgba(179,0,53,0.35)] bg-[rgba(179,0,53,0.06)] px-4 py-3"
          >
            <div className="flex items-center gap-2 text-[var(--js-red)]">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm font-semibold">
                Please review the highlighted values
              </span>
            </div>
            <ul className="mt-1 list-disc pl-9 text-sm font-medium text-[var(--js-red)]">
              {banner.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5 items-start">
          {/* ============ COLUMN 1 ============ */}
          <div>
            <SectionCard title="Product Properties" testId="panel-product-properties">
              <CalcFieldRow label="Product Name" testId="product-name" kind="text-input"
                value={inputs.productName} onChange={set("productName")} />
              <CalcFieldRow label="Thermal Properties" testId="thermal-properties" kind="select"
                value={inputs.thermalProperty} onChange={set("thermalProperty")}
                options={productOptions} placeholder="Select product" />
              <CalcFieldRow label="Inlet Temperature" quantity="temp" testId="inlet-temp" kind="number-input"
                value={inputs.inletTemp} onChange={set("inletTemp")}
                onBlur={blur("inletTemp")} error={fieldErrors.inletTemp}
                note={notes.inletTemp} />
              <CalcFieldRow label="Outlet Temperature" quantity="temp" testId="outlet-temp" kind="number-input"
                value={inputs.outletTemp} onChange={set("outletTemp")}
                onBlur={blur("outletTemp")} error={fieldErrors.outletTemp}
                note={notes.outletTemp} />
              <CalcFieldRow label="Density" quantity="density" testId="density" kind="number-input"
                value={inputs.density} onChange={set("density")}
                onBlur={blur("density")} error={fieldErrors.density}
                note={notes.density} />
              <CalcFieldRow label="Contact %" unit="%" testId="contact-pc" kind="number-input"
                value={inputs.contactPc} onChange={set("contactPc")}
                onBlur={blur("contactPc")} error={fieldErrors.contactPc}
                note={notes.contactPc} />
            </SectionCard>

            <SectionCard title="Refrigerant" testId="panel-refrigerant">
              {/* Refrigerant Type selector */}
              <CalcFieldRow label="Refrigerant Type" testId="refrigerant-type" kind="select"
                value={inputs.refrigerantType} onChange={set("refrigerantType")}
                options={[
                  { value: "Primary", label: "Primary" },
                  { value: "Secondary", label: "Secondary" },
                ]}
                placeholder="Select type" />

              {/* Primary: Refrigerant + Evaporation Temp */}
              {!isSecondary && (
                <>
                  <CalcFieldRow label="Refrigerant" testId="refrigerant" kind="select"
                    value={inputs.refrigerant} onChange={set("refrigerant")}
                    options={primaryRefrigerantOptions} placeholder="Select refrigerant" />
                  <CalcFieldRow label="Evaporation Temp." quantity="temp" testId="evap-temp" kind="number-input"
                    value={inputs.evapTemp} onChange={set("evapTemp")}
                    onBlur={blur("evapTemp")} error={fieldErrors.evapTemp}
                note={notes.evapTemp} />
                </>
              )}

              {/* Secondary: Heat Transfer Fluid + Fluid Inlet/Outlet */}
              {isSecondary && (
                <>
                  <CalcFieldRow label="Heat Transfer Fluid" testId="heat-transfer-fluid" kind="select"
                    value={inputs.heatTransferFluid} onChange={set("heatTransferFluid")}
                    options={heatTransferFluidOptions} placeholder="Select fluid" />
                  <FluidInletOutletRow
                    htfInletTemp={inputs.htfInletTemp}
                    htfDeltaT={inputs.htfDeltaT}
                    htfOutletTemp={htfOutletTemp}
                    onInletChange={set("htfInletTemp")}
                    onDeltaTChange={set("htfDeltaT")}
                    onDeltaTBlur={blur("htfDeltaT")}
                    units={uiUnits}
                    deltaTNote={notes.htfDeltaT}
                    inletError={fieldErrors.htfInletTemp}
                    deltaTError={fieldErrors.htfDeltaT}
                  />
                </>
              )}
            </SectionCard>

            <SectionCard title="Freezer Sizing" testId="panel-freezer-sizing">
              <CalcFieldRow label="No. of Freezers" unit="-" testId="n-freezers" kind="number-input"
                value={inputs.nFreezers} onChange={set("nFreezers")} error={fieldErrors.nFreezers} />
              <CalcFieldRow label="No of Stations" unit="-" testId="n-stations" kind="number-input"
                value={inputs.nStations} onChange={set("nStations")}
                onBlur={blur("nStations")} error={fieldErrors.nStations}
                note={notes.nStations} />
              <CalcFieldRow label="Station Length" quantity="length" testId="station-length" kind="number-input"
                value={inputs.lStation} onChange={set("lStation")}
                onBlur={blur("lStation")} error={fieldErrors.lStation}
                note={notes.lStation} />
              <CalcFieldRow label="Station Depth" quantity="length" testId="station-depth" kind="number-input"
                value={inputs.dStation} onChange={set("dStation")}
                onBlur={blur("dStation")} error={fieldErrors.dStation}
                note={notes.dStation} />
              <CalcFieldRow label="Station Thickness" quantity="length" testId="station-thickness" kind="number-input"
                value={inputs.tStation} onChange={set("tStation")}
                onBlur={blur("tStation")} error={fieldErrors.tStation}
                note={notes.tStation} />
              <CalcFieldRow label="Dividers" unit="-" testId="n-dividers" kind="number-input"
                value={inputs.nDividers} onChange={set("nDividers")} error={fieldErrors.nDividers} />
            </SectionCard>

            <SectionCard title="Freezer Physical Dimensions"
              badge={vpf ? undefined : "Calculating…"}
              badgeTone="info"
              testId="panel-freezer-physical-dimensions">
              <CalcFieldRow label="Freezer Length" quantity="length" testId="freezer-length" kind="output"
                displayValue={vpfQInt(vpf?.OALength, "length")} />
              <CalcFieldRow label="Freezer Depth" quantity="length" testId="freezer-depth" kind="output"
                displayValue={vpfQInt(vpf?.OADepth, "length")} />
              <CalcFieldRow label="Freezer Height (Closed)" quantity="length" testId="freezer-height-closed" kind="output"
                displayValue={vpfQInt(vpf?.OAHeightLowered, "length")} />
              <CalcFieldRow label="Freezer Height (Open)" quantity="length" testId="freezer-height-open" kind="output"
                displayValue={vpfQInt(vpf?.OAHeightRaised, "length")} />
              <CalcFieldRow label="Freezer Weight" quantity="mass" testId="freezer-weight" kind="deferred" />
            </SectionCard>
          </div>

          {/* ============ COLUMN 2 ============ */}
          <div>
            <SectionCard title="Freezer Product Load" testId="panel-freezer-product-load">
              <CalcFieldRow label="Block Size" quantity="length" testId="block-size" kind="output"
                displayValue={r ? formatBlockSize(r.blockSize, uiUnits) : "\u2014"} />
              <CalcFieldRow label="Est. Block Weight" quantity="mass" testId="est-block-weight" kind="output"
                displayValue={outQ(r?.estBlockWeight, "mass", 1)} />
              <CalcFieldRow label="Blocks per Station" unit="-" testId="blocks-per-station" kind="output"
                displayValue={r ? String(r.blocksPerStation) : "\u2014"} />
              <CalcFieldRow label="Est. Station Weight" quantity="mass" testId="est-station-weight" kind="output"
                displayValue={outQ(r?.estStationWeight, "mass", 1)} />
              <CalcFieldRow label="Weight per Batch" quantity="mass" testId="weight-per-batch" kind="output"
                displayValue={outQ(r?.weightPerBatch, "mass", 1)} />
            </SectionCard>

            <SectionCard title="Freezing & Cycle Times" testId="panel-freezing-cycle-times">
              <CalcFieldRow label="Time to close / lower" unit="sec" testId="closing-time" kind="output"
                displayValue={outInt(r?.closingTime)} />
              <CalcFieldRow label="Loading Time" unit="sec/stn" testId="loading-time" kind="number-input"
                value={inputs.loadingTime} onChange={set("loadingTime")}
                onBlur={blur("loadingTime")} error={fieldErrors.loadingTime}
                note={notes.loadingTime} />
              <SplitOutputRow label="Est. Freezing Time" unit="min" testId="est-freezing-time"
                calcDisplay={r ? outInt(r.estFreezingTimeCalc) : "\u2014"}
                calcRaw={r?.estFreezingTimeCalc ?? null}
                overrideValue={inputs.overrideFreezeTime}
                isManual={manual.overrideFreezeTime}
                onToggle={(on, prefill) => setManual("overrideFreezeTime", on, prefill)}
                onChange={set("overrideFreezeTime")}
                error={fieldErrors.overrideFreezeTime} />
              <SplitOutputRow label="Defrosting" unit="min" testId="defrosting"
                calcDisplay={r ? out(r.defrosting, 1) : "\u2014"}
                calcRaw={r?.defrosting ?? null}
                overrideValue={inputs.overrideDefrostMin}
                isManual={manual.overrideDefrostMin}
                onToggle={(on, prefill) => setManual("overrideDefrostMin", on, prefill)}
                onChange={set("overrideDefrostMin")}
                decimals={1}
                error={fieldErrors.overrideDefrostMin} />
              <CalcFieldRow label="Time to open / raise" unit="sec" testId="opening-time" kind="output"
                displayValue={outInt(r?.openingTime)} />
              <CalcFieldRow label="Unloading" unit="sec/stn" testId="unloading-time" kind="number-input"
                value={inputs.unloadingTime} onChange={set("unloadingTime")}
                onBlur={blur("unloadingTime")} error={fieldErrors.unloadingTime}
                note={notes.unloadingTime} />
              <CalcFieldRow label="Wait / Misc" unit="min" testId="wait-misc" kind="number-input"
                value={inputs.waitMisc} onChange={set("waitMisc")} error={fieldErrors.waitMisc} />

              {/* Total Loading Time */}
              <div className="mt-2 rounded-md bg-[rgba(57,103,143,0.10)] px-2 py-1.5">
                <div className="flex items-center gap-2">
                  <span className="w-[166px] shrink-0 text-right text-[12px] font-semibold text-[var(--js-navy)]">
                    Total Loading Time
                  </span>
                  <div className="min-w-0 flex-1">
                    <div data-testid="total-loading-time-output"
                      className="flex h-8 items-center justify-center rounded-md border border-[rgba(57,103,143,0.25)] bg-[var(--js-light-blue)] px-2 text-[12px] font-semibold text-[var(--js-navy)] tabular-nums">
                      {totalLoadingMin !== null ? fmtSmart(totalLoadingMin, 1) : "\u2014"}
                    </div>
                  </div>
                  <span className="w-[46px] shrink-0 pl-1 text-[12px] font-semibold text-[var(--js-navy)]">min</span>
                </div>
              </div>

              {/* Total Unloading Time */}
              <div className="mt-1 rounded-md bg-[rgba(57,103,143,0.10)] px-2 py-1.5">
                <div className="flex items-center gap-2">
                  <span className="w-[166px] shrink-0 text-right text-[12px] font-semibold text-[var(--js-navy)]">
                    Total Unloading Time
                  </span>
                  <div className="min-w-0 flex-1">
                    <div data-testid="total-unloading-time-output"
                      className="flex h-8 items-center justify-center rounded-md border border-[rgba(57,103,143,0.25)] bg-[var(--js-light-blue)] px-2 text-[12px] font-semibold text-[var(--js-navy)] tabular-nums">
                      {totalUnloadingMin !== null ? fmtSmart(totalUnloadingMin, 1) : "\u2014"}
                    </div>
                  </div>
                  <span className="w-[46px] shrink-0 pl-1 text-[12px] font-semibold text-[var(--js-navy)]">min</span>
                </div>
              </div>

              {/* Total Cycle Time - navy highlight bar */}
              <div className="mt-2 rounded-md bg-[var(--js-navy)] px-2 py-1.5">
                <div className="flex items-center gap-2">
                  <span className="w-[166px] shrink-0 text-right text-[12px] font-bold text-white">
                    Total Cycle Time
                  </span>
                  <div className="min-w-0 flex-1">
                    <div data-testid="total-cycle-time-output"
                      className="flex h-8 items-center justify-center rounded-md border border-white/20 bg-white/12 px-2 text-[12px] font-bold text-white tabular-nums">
                      {out(r?.totalCycleTime, 1)}
                    </div>
                  </div>
                  <span className="w-[46px] shrink-0 pl-1 text-[12px] font-bold text-white">min</span>
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Freezing / Throughput Capacity" testId="panel-throughput">
              <CalcFieldRow label="Nominal Capacity each" quantity="capacity" testId="nominal-capacity-each" kind="output"
                displayValue={outQ(r?.nominalCapacityEach, "capacity", 1)} />
              <CalcFieldRow label="Nominal Capacity Total" quantity="capacity" testId="nominal-capacity-total" kind="output"
                displayValue={outQ(r?.nominalCapacityTotal, "capacity", 1)} />
              <CalcFieldRow label="...per day, 24/7" quantity="throughput" testId="per-day" kind="output"
                displayValue={outQInt(r?.perDay24_7, "throughput")} />

              <CycleRow label={`${r ? r.cyclesPer12Hrs : "#"} cycles per 12 h`} testId="cycles-12"
                kg={r?.throughputPer12Hrs} has={!!r} units={uiUnits} />
              <CycleRow label={`${r ? r.cyclesPer24Hrs : "#"} cycles per 24 h`} testId="cycles-24"
                kg={r?.throughputPer24Hrs} has={!!r} units={uiUnits} />

              {/* editable window */}
              <div className="py-[3px]">
                <div className="flex items-center gap-2">
                  {/* Label yields to the inner controls — clips if narrow */}
                  <label htmlFor="cycles-window-field" className="w-[166px] min-w-0 overflow-hidden whitespace-nowrap text-right text-[12px] font-medium tracking-tight text-[var(--js-text-2)]">
                    {r ? r.cyclesPerWindow : "#"} cycles per
                  </label>
                  {/* min-w keeps h-input + label + output from collapsing */}
                  <div className="flex min-w-[124px] flex-1 items-center gap-1.5">
                    <input
                      id="cycles-window-field"
                      data-testid="cycles-window-input"
                      value={inputs.cyclesWindowHrs}
                      inputMode="decimal"
                      onChange={(e) => set("cyclesWindowHrs")(e.target.value)}
                      title={fieldErrors.cyclesWindowHrs}
                      aria-invalid={fieldErrors.cyclesWindowHrs ? true : undefined}
                      aria-describedby={fieldErrors.cyclesWindowHrs ? "cycles-window-error" : undefined}
                      className={`h-8 w-12 shrink-0 rounded-md border bg-[var(--calc-input-bg)] px-1 text-center text-[12px] font-medium text-[var(--js-text)] focus-visible:border-[var(--js-bright-blue)] ${FOCUS_RING} ${
                        fieldErrors.cyclesWindowHrs ? "border-[var(--js-red)]" : "border-[rgba(51,66,79,0.18)]"
                      }`}
                    />
                    <span className="shrink-0 text-[12px] font-medium text-[var(--js-text-2)]">h</span>
                    <div data-testid="cycles-window-output"
                      className="flex h-8 min-w-[44px] flex-1 items-center justify-center rounded-md border border-[rgba(51,66,79,0.12)] bg-[var(--calc-output-bg)] px-2 text-[12px] font-semibold text-[var(--js-text)] tabular-nums overflow-hidden">
                      {outQInt(r?.throughputPerWindow, "throughput")}
                    </div>
                  </div>
                  <span className="w-[46px] shrink-0 pl-1 text-[12px] font-semibold text-[var(--js-text-2)]">{u("throughput")}</span>
                </div>
                {fieldErrors.cyclesWindowHrs && (
                  <div className="mt-0.5 flex items-center justify-center gap-1">
                    <AlertCircle className="h-3 w-3 shrink-0 text-[var(--js-red)]" aria-hidden="true" />
                    <span id="cycles-window-error" data-testid="cycles-window-error" className="text-[11px] font-medium text-[var(--js-red)]">
                      {fieldErrors.cyclesWindowHrs}
                    </span>
                  </div>
                )}
              </div>

              {/* In Depth Review */}
              <div className="mt-2 rounded-md border border-[rgba(51,66,79,0.12)] bg-[#e9edf2] px-2 py-1.5">
                <div className="flex items-center gap-2">
                  <span className="shrink-0 whitespace-nowrap text-right text-[12px] font-medium text-[var(--js-text-2)]">
                    In Depth Review ➔
                  </span>
                  <button
                    data-testid="production-gantt-button"
                    type="button"
                    onClick={() => navigate("/gantt")}
                    title="Open Production Gantt Chart"
                    className="h-8 min-w-0 flex-1 cursor-pointer truncate whitespace-nowrap rounded-md bg-[var(--js-purple)] px-3 text-[12px] font-semibold text-white transition-opacity hover:opacity-90 active:opacity-75"
                  >
                    Production Gantt Chart
                  </button>
                </div>
              </div>
            </SectionCard>
          </div>

          {/* ============ COLUMN 3 ============ */}
          <div>
            <SectionCard title="Refrigeration Details" testId="panel-refrigeration-details">
              <CalcFieldRow label="Cooling Load per Freezer" quantity="power" testId="cooling-load-per-freezer" kind="output"
                displayValue={outQ(r?.coolingLoadPerFreezer, "power", 1)} />
              <CalcFieldRow label="Est. Overall Cooling Load" quantity="power" testId="overall-cooling-load" kind="output"
                displayValue={outQ(r?.overallCoolingLoad, "power", 1)} />
              <CalcFieldRow label="Recirculation Rate" unit="-" testId="recirculation-rate" kind="output"
                displayValue={
                  r ? (isSecondary
                    ? recircDeltaT(inputs.htfDeltaT, uiUnits)
                    : fmtRecirc(r.recirculationRate))
                  : "\u2014"
                } />
              <CalcFieldRow label="Max. Defrost Capacity" quantity="power" testId="max-defrost-capacity" kind="output"
                displayValue={outQ(r?.maxDefrostCapacity, "power", 1)} />
              <CalcFieldRow label="Peak Cooling Load" quantity="power" testId="peak-cooling-load" kind="output"
                displayValue={outQ(r?.peakCoolingLoad, "power", 1)} />
              {/* Labels adapt for secondary (sensible-heat fluid flow) */}
              <CalcFieldRow label={isSecondary ? "Fluid Mass Flow" : "Ref. Mass Flow"}
                quantity="massFlow" testId="ref-mass-flow" kind="output"
                displayValue={outQInt(r?.refMassFlow, "massFlow")} />
              <CalcFieldRow label={isSecondary ? "Fluid Vol. Flow" : "Ref. Volume Flow"}
                quantity="volFlow" testId="ref-volume-flow" kind="output"
                displayValue={outQInt(r?.refVolumeFlow, "volFlow")} />
              <CalcFieldRow label="Internal Volume" quantity="volume" testId="interval-volume" kind="output"
                displayValue={vpfQInt(vpf?.InternalVolume, "volume")} />
              <CalcFieldRow label="Orifice Diameter" quantity="lengthFine" testId="orifice-diameter" kind="output"
                displayValue={outQ(r?.orificeDiameter, "lengthFine", 1)} />
              <CalcFieldRow label="Orifice dP" quantity="pressure" testId="orifice-dp" kind="output"
                displayValue={outQ(r?.orificeDP, "pressure", 3)} />
              <CalcFieldRow label="Product Pressure" quantity="pressure" testId="product-pressure" kind="output"
                displayValue={vpf ? outQ(vpf.pPressure, "pressure", 3) : "\u2014"} />
            </SectionCard>

            {/* ─── Save / Open Project ─── */}
            <SectionCard title="Save / Open Project" testId="panel-save-open">
              <div className="flex flex-col gap-3 py-1">

                {/* Filename preview */}
                <div className="rounded-md bg-[rgba(57,103,143,0.07)] px-3 py-2">
                  <p className="text-[10.5px] font-medium text-[var(--js-text-2)] mb-0.5">File name</p>
                  <p className="text-[12px] font-semibold text-[var(--js-navy)] truncate">
                    {!r ? "— waiting for first calculation —"
                      : current ? defaultFilename : "— results out of date —"}
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="save-project-button"
                    onClick={handleSave}
                    disabled={!current}
                    className="flex flex-1 items-center justify-center gap-1.5 h-8 rounded-md bg-[var(--js-blue)] text-white text-[12px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#2d5577] transition-colors"
                    title={current ? "Download project as JSON"
                      : status === "error" ? "Can't reach the server — save is available once results update"
                      : "Fix the highlighted inputs to save"}
                  >
                    <Save className="h-3.5 w-3.5" />
                    Save Project
                  </button>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex flex-1 items-center justify-center gap-1.5 h-8 rounded-md border border-[var(--js-blue)] text-[var(--js-blue)] text-[12px] font-semibold hover:bg-[var(--js-light-blue)] transition-colors"
                    title="Open a previously saved project JSON"
                  >
                    <FolderOpen className="h-3.5 w-3.5" />
                    Open Project
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    className="hidden"
                    onChange={handleOpen}
                  />
                </div>

                {/* Format note + Reset */}
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[10.5px] text-[var(--js-text-2)] leading-relaxed">
                    JSON — split into <b>inputs</b> (restored on open) and <b>outputs</b> (for documentation).
                    Each field includes variable name, description, value and unit.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm("Reset all inputs to factory defaults? This will clear your saved session.")) {
                        clearInputs();
                      }
                    }}
                    className="flex-shrink-0 text-[10.5px] font-medium text-[var(--js-text-2)] hover:text-[var(--js-red)] underline decoration-dotted transition-colors cursor-pointer"
                    title="Reset all inputs to factory defaults and clear saved session"
                  >
                    Reset to defaults
                  </button>
                </div>
              </div>
            </SectionCard>

            {/* ─── Export Report (PDF) ─── */}
            <ExportReportCard
              modelCode={r?.modelCode}
              nFreezers={inputs.nFreezers}
              ready={!!r}
              current={current}
              page="calculator"
            />

            <div className="rounded-lg border border-[var(--js-border)] bg-white px-4 py-3">
              <div className="flex items-start gap-2 text-[var(--js-text-2)]">
                <Info className="mt-[3px] h-3.5 w-3.5 shrink-0 text-[var(--js-blue)]" />
                <p className="text-[12px] font-medium leading-relaxed">
                  Refrigerant properties are computed live via <b>CoolProp</b>. Results
                  auto-refresh as inputs change.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// Throughput cycle row: dynamic "{N} cycles per X h" label + kg throughput
function CycleRow({
  label,
  testId,
  kg,
  has,
  units,
}: {
  label: string;
  testId: string;
  kg?: number;
  has: boolean;
  units: UnitSystem;
}) {
  return (
    <div className="py-[3px]">
      <div className="flex items-center gap-2">
        <span className="w-[166px] shrink-0 text-right text-[12px] font-medium tracking-tight text-[var(--js-text-2)]">
          {label}
        </span>
        <div className="min-w-0 flex-1">
          <div data-testid={`${testId}-output`}
            className="flex h-8 items-center justify-center rounded-md border border-[rgba(51,66,79,0.12)] bg-[var(--calc-output-bg)] px-2 text-[12px] font-semibold text-[var(--js-text)] tabular-nums">
            {has ? formatQty(kg, "throughput", units, fmtInt) : "\u2014"}
          </div>
        </div>
        <span className="w-[46px] shrink-0 pl-1 text-[12px] font-semibold text-[var(--js-text-2)]">{unitLabel("throughput", units)}</span>
      </div>
    </div>
  );
}

// Fluid Inlet / Outlet row for secondary refrigerants
// Layout: [Label] [-40] [+] [3] K [=] [-37] [°C]
function FluidInletOutletRow({
  htfInletTemp,
  htfDeltaT,
  htfOutletTemp,
  onInletChange,
  onDeltaTChange,
  onDeltaTBlur,
  inletError,
  deltaTError,
  deltaTNote,
  units,
}: {
  htfInletTemp: string;
  htfDeltaT: string;
  htfOutletTemp: number | null;
  onInletChange: (v: string) => void;
  onDeltaTChange: (v: string) => void;
  onDeltaTBlur?: () => void;
  inletError?: string;
  deltaTError?: string;
  deltaTNote?: string;
  units: UnitSystem;
}) {
  // Inlet is a temperature, ΔT a temperature difference — both SI in state
  const inlet = useUnitInput(htfInletTemp, "temp", onInletChange);
  const delta = useUnitInput(htfDeltaT, "tempDelta", onDeltaTChange, onDeltaTBlur);
  const shownError = localiseMessage(inletError || deltaTError, units);
  const shownNote = localiseNote(deltaTNote, "tempDelta", units);

  /* Each box has a 44 px minimum so values like "−40" are always legible.
     flex-1 shares any surplus space equally.                               */
  const INPUT_CLS =
    `h-8 min-w-[44px] flex-1 rounded-md border bg-[var(--calc-input-bg)] border-[rgba(51,66,79,0.18)] px-1 text-center text-[12px] font-medium text-[var(--js-text)] focus-visible:border-[var(--js-bright-blue)] ${FOCUS_RING}`;
  const OUTPUT_CLS =
    "flex h-8 min-w-[44px] flex-1 items-center justify-center rounded-md border border-[rgba(51,66,79,0.12)] bg-[var(--calc-output-bg)] px-1 text-[12px] font-semibold text-[var(--js-text)] tabular-nums overflow-hidden";

  /* min-w-0 (overrides flex's 'auto' floor) + no shrink-0 = the label yields
     to the box group first.  overflow-hidden + whitespace-nowrap = right-
     aligned text clips from the left ("uid Inlet / Outlet") not the right.  */
  const LABEL_CLS =
    "w-[166px] min-w-0 overflow-hidden whitespace-nowrap text-right text-[12px] font-medium leading-tight tracking-tight text-[var(--js-text-2)]";
  const UNIT_CLS = "w-[46px] shrink-0 pl-1 text-[12px] font-semibold text-[var(--js-text-2)]";
  const SEP_CLS = "shrink-0 text-[12px] font-semibold text-[var(--js-text-2)]";

  const outletDisplay = formatQty(htfOutletTemp, "temp", units, (x) => fmt(x, 1));

  return (
    <div className="py-[3px]">
      <div className="flex items-center gap-2">
        <label htmlFor="htf-inlet-temp-field" className={LABEL_CLS}>Fluid Inlet / Outlet</label>
        {/* min-w-[180px] guarantees the three boxes + separators always have
            enough room.  If the row is too narrow the label shrinks first.   */}
        <div className="flex min-w-[180px] flex-1 items-center gap-1">
          <input
            id="htf-inlet-temp-field"
            aria-invalid={inletError ? true : undefined}
            aria-describedby={inletError ? "htf-row-error" : undefined}
            data-testid="htf-inlet-temp-input"
            value={inlet.value}
            inputMode="decimal"
            onChange={(e) => inlet.onChange(e.target.value)}
            onBlur={inlet.onBlur}
            className={`${INPUT_CLS} ${inletError ? "border-[var(--js-red)] shadow-[0_0_0_3px_rgba(179,0,53,0.16)]" : ""}`}
          />
          <span className={SEP_CLS}>+</span>
          <input
            aria-label="Fluid temperature rise (ΔT)"
            aria-invalid={deltaTError ? true : undefined}
            aria-describedby={deltaTError ? "htf-row-error" : undefined}
            data-testid="htf-delta-t-input"
            value={delta.value}
            inputMode="decimal"
            onChange={(e) => delta.onChange(e.target.value)}
            onBlur={delta.onBlur}
            className={`${INPUT_CLS} ${deltaTError ? "border-[var(--js-red)] shadow-[0_0_0_3px_rgba(179,0,53,0.16)]" : ""}`}
          />
          <span className="shrink-0 text-[11px] font-bold text-[var(--js-text-2)]">{unitLabel("tempDelta", units)}</span>
          <span className={SEP_CLS}>=</span>
          <div data-testid="htf-outlet-temp-output" className={OUTPUT_CLS}>
            {outletDisplay}
          </div>
        </div>
        <span className={UNIT_CLS}>{unitLabel("temp", units)}</span>
      </div>
      {(inletError || deltaTError) && (
        <div className="mt-0.5 flex items-center justify-center gap-1">
          <AlertCircle className="h-3 w-3 shrink-0 text-[var(--js-red)]" />
          <span id="htf-row-error" className="text-[11px] font-medium text-[var(--js-red)]">
            {shownError}
          </span>
        </div>
      )}
      {!inletError && !deltaTError && shownNote && <FieldNote id="htf-row-note">{shownNote}</FieldNote>}
    </div>
  );
}

// ── Split Output Row ─────────────────────────────────────────────────────────
// Displays a calculated output with an AUTO/MAN pill.
// AUTO (blue): shows the calculated value read-only.
// MAN (amber): shows an editable input in the pale-cream override palette.
// Manual mode is an explicit flag (only the pill changes it): clearing the MAN
// input keeps MAN and shows a validation message instead of flipping to AUTO.
// `decimals` = places accepted in MAN (0 = whole minutes); limits and the
// error message come from lib/limits via lib/validators.
function SplitOutputRow({
  label, unit, testId, calcDisplay, calcRaw, overrideValue, isManual, onToggle, onChange, decimals = 0, error,
}: {
  label: string;
  unit: string;
  testId: string;
  calcDisplay: string;        // formatted value shown in AUTO mode
  calcRaw: number | null;     // raw number used to pre-populate on first switch to MAN
  overrideValue: string;      // from CalcInputs — the MAN value
  isManual: boolean;          // MAN mode (explicit — not derived from the value)
  onToggle: (on: boolean, prefill: string) => void;
  onChange: (v: string) => void;
  decimals?: number;
  error?: string;
}) {
  const handlePill = () => {
    if (isManual) {
      onToggle(false, "");   // back to AUTO — clear override
    } else {
      // Switch to MAN: pre-populate with the calculated value, rounded to the
      // precision MAN accepts
      const f = 10 ** decimals;
      onToggle(true, calcRaw !== null ? String(Math.round(calcRaw * f) / f) : "");
    }
  };

  // Shared container: one unified border/background so the whole row looks
  // like a single box, aligned with all other field rows.
  const containerCls = isManual
    ? (error ? "bg-[#FFF8D6] border-[var(--js-red)]" : "bg-[#FFF8D6] border-[#F5A623]")
    : "bg-[var(--calc-output-bg)] border-[rgba(51,66,79,0.12)]";

  const pillCls = isManual
    ? "bg-[#FFF8D6] border-l-[#F5A623] text-[#C87500]"
    : "bg-[rgba(57,103,143,0.08)] border-l-[rgba(51,66,79,0.12)] text-[var(--js-blue)]";

  return (
    <div className="py-[3px]">
      <div className="flex items-center gap-2">
        <label htmlFor={isManual ? `${testId}-field` : undefined} id={`${testId}-label`}
          className="w-[166px] shrink-0 text-right text-[12px] font-medium leading-tight tracking-tight text-[var(--js-text-2)]">
          {label}
        </label>
        {/* focus-within: the inner input is borderless, so the box draws the ring */}
        <div className={`flex min-w-0 flex-1 h-8 rounded-md border overflow-hidden transition-colors focus-within:ring-2 focus-within:ring-[var(--js-bright-blue)] focus-within:ring-offset-1 ${containerCls}`}>
          {/* Value / input area — flex-1 so it fills the full width like other boxes */}
          {isManual ? (
            <input
              id={`${testId}-field`}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? `${testId}-error` : undefined}
              data-testid={`${testId}-input`}
              inputMode="decimal"
              value={overrideValue}
              onChange={e => {
                // Positive numbers only — digits (plus "." when decimals > 0).
                // A malformed entry like "1.2.3" is kept so it shows an error.
                const re = decimals > 0 ? /[^0-9.]/g : /[^0-9]/g;
                onChange(e.target.value.replace(re, ""));
              }}
              className="flex-1 min-w-0 text-center text-[12px] font-semibold bg-transparent border-none outline-none text-[#C87500] tabular-nums"
            />
          ) : (
            <div
              data-testid={`${testId}-output`}
              aria-labelledby={`${testId}-label`}
              className="flex-1 flex items-center justify-center text-[12px] font-semibold tabular-nums text-[var(--js-text)]"
            >
              {calcDisplay}
            </div>
          )}

          {/* Mode pill — fixed width, separated by a vertical divider */}
          <button
            type="button"
            onClick={handlePill}
            className={`w-[40px] flex-shrink-0 flex items-center justify-center border-l text-[9px] font-extrabold tracking-wider transition-colors cursor-pointer ${pillCls}`}
            title={isManual ? "Return to calculated value" : "Override with manual value"}
            aria-label={`${label}: ${isManual ? "manual — return to calculated value" : "automatic — override with manual value"}`}
          >
            {isManual ? "MAN" : "AUTO"}
          </button>
        </div>
        <span className="w-[46px] shrink-0 pl-1 text-[12px] font-semibold text-[var(--js-text-2)]">
          {unit}
        </span>
      </div>
      {isManual && error && (
        <div className="mt-0.5 flex items-center justify-center gap-1">
          <AlertCircle className="h-3 w-3 shrink-0 text-[var(--js-red)]" />
          <span id={`${testId}-error`} data-testid={`${testId}-error`} className="text-[11px] font-medium text-[var(--js-red)]">
            {error}
          </span>
        </div>
      )}
    </div>
  );
}

/** Secondary recirculation read-out: the fluid ΔT, e.g. "3K ΔT" / "5.4°F ΔT". */
function recircDeltaT(htfDeltaT: string, units: UnitSystem): string {
  const clean = stripOverride(htfDeltaT);
  if (units === "SI") return `${clean}K \u0394T`;
  return `${siInputToUS(clean, "tempDelta")}\u00b0F \u0394T`;
}
